import { useNavigate } from 'react-router-dom';
import type { CoverageHistoryTrendPoint } from '@/components/smartai/api/getCoverageHistory';
import { cn } from '@/lib/utils';
import { coveragePctClass } from './coverage-dashboard-layout';
import {
  formatProcessedAt,
  formatRunSourceType,
  pctFromSummary,
} from './coverage-history-utils';

interface CoverageHistoryRunsTableProps {
  points: CoverageHistoryTrendPoint[];
  variant?: 'build' | 'run';
  rowHref?: (point: CoverageHistoryTrendPoint) => string | undefined;
}

export default function CoverageHistoryRunsTable({
  points,
  variant = 'build',
  rowHref,
}: CoverageHistoryRunsTableProps) {
  const navigate = useNavigate();
  const rows = [...points].reverse();

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[36rem] border-collapse text-left text-[0.8125rem]">
          <thead>
            <tr className="border-b border-[var(--inv-border)] bg-[var(--inv-surface-muted,#f8f6f3)]">
              <th className="px-4 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                Processed
              </th>
              {variant === 'build' ? (
                <th className="px-4 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                  Scan
                </th>
              ) : (
                <th className="px-4 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                  Run
                </th>
              )}
              <th className="px-4 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                {variant === 'run' ? 'Session' : 'Build'}
              </th>
              <th className="px-4 py-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                Source
              </th>
              <th className="px-4 py-2.5 text-right text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                LOC %
              </th>
              <th className="px-4 py-2.5 text-right text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]">
                Inventory %
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const loc = pctFromSummary(row.summary, 'lines');
              const inv = pctFromSummary(row.summary, 'inventory');
              const key = row.runId ?? `${row.processedAt}-${row.runNumber}`;
              const isLatest = index === 0;
              const href = rowHref?.(row);
              const rowClass = cn(
                'border-b border-[var(--inv-border-soft)] transition-colors last:border-b-0 hover:bg-[var(--inv-surface-muted)]/50',
                isLatest && 'bg-[var(--inv-accent-soft,rgba(0,70,184,0.04))]',
                href && 'cursor-pointer',
              );
              const cells = (
                <>
                  <td className="px-4 py-2.5 tabular-nums text-[var(--inv-text)]">
                    {formatProcessedAt(row.processedAt)}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[0.75rem] text-[var(--inv-text-secondary)]">
                    {variant === 'run'
                      ? row.runNumber != null
                        ? `#${row.runNumber}`
                        : '—'
                      : row.scanNumber != null
                        ? `#${row.scanNumber}`
                        : row.runNumber ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--inv-text)]">
                    {variant === 'run'
                      ? row.sessionId ?? row.buildId ?? '—'
                      : row.buildNumber != null
                        ? row.buildNumber
                        : row.sessionId
                          ? row.sessionId
                          : row.buildId ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 capitalize text-[var(--inv-text-secondary)]">
                    {variant === 'run'
                      ? formatRunSourceType(row.sourceType)
                      : row.sourceType ?? 'combined'}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-2.5 text-right tabular-nums',
                      loc != null ? coveragePctClass(loc) : 'text-[var(--inv-text-muted)]',
                    )}>
                    {loc != null ? `${loc.toFixed(1)}%` : '—'}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-2.5 text-right tabular-nums',
                      inv != null ? coveragePctClass(inv) : 'text-[var(--inv-text-muted)]',
                    )}>
                    {inv != null ? `${inv.toFixed(1)}%` : '—'}
                  </td>
                </>
              );
              return (
                <tr
                  key={key}
                  className={rowClass}
                  onClick={
                    href
                      ? () => {
                          navigate(href);
                        }
                      : undefined
                  }
                  onKeyDown={
                    href
                      ? e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(href);
                          }
                        }
                      : undefined
                  }
                  tabIndex={href ? 0 : undefined}
                  role={href ? 'link' : undefined}>
                  {cells}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
